import AnimateOnScroll from "./AnimateOnScroll";

const REGISTER_URL =
  "https://id.production.envizage.me/realms/c71accc6-d6a8-400b-8956-142a1fb9c57b/protocol/openid-connect/auth?client_id=b414c6ec-7a9e-4d43-af8e-c863b2623f56&redirect_uri=https%3A%2F%2Fcfp.envizage.ai%2F&state=37b650fb-2cc2-40a3-b5f0-95ad139fc3cf&response_mode=fragment&response_type=code&scope=openid&nonce=cc28099f-402a-410b-ae1f-eb76b93f3d3d&code_challenge=KSI36IXuIVPHe71mh52L1RbODywaZfDuH-gVpctXezM&code_challenge_method=S256";

const responsibilities = [
  {
    icon: "ri-survey-line",
    title: "Fortnightly Feedback",
    desc: "Complete a 5-minute survey or screen recording reacting to features we're actively reviewing.",
    freq: "Fortnightly",
    effort: "~5 min",
  },
  {
    icon: "ri-bug-line",
    title: "Bug Reporting",
    desc: "Flag any hiccups or poor experiences you encounter during regular use of the platform.",
    freq: "Ongoing",
    effort: "As needed",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "Design Sprints",
    desc: "Occasionally vote on UI/UX mockups to help us choose the most intuitive direction.",
    freq: "Occasional",
    effort: "~10 min",
  },
];

const perks = [
  {
    icon: "ri-price-tag-3-line",
    text: "£3.25/month per end user through 2026 (75% off £12.99)",
  },
  {
    icon: "ri-roadmap-line",
    text: "Direct influence over the product roadmap",
  },
  {
    icon: "ri-timer-line",
    text: "Fortnightly feedback sessions — just 5–10 minutes to shape what gets built",
  },
  {
    icon: "ri-flask-line",
    text: "First access to every new feature before anyone else",
  },
  {
    icon: "ri-lock-2-line",
    text: "Rate locked at £3.25/month per end user through 2026",
  },
  {
    icon: "ri-customer-service-2-line",
    text: "Direct email support from the product team",
  },
];

export default function CoCreatorSection() {
  const scrollToRegister = () => {
    const el = document.querySelector("#register");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const openCtaUrl = () => {
    window.open(REGISTER_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="co-creator" className="bg-white py-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-5 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5">
              <span className="w-1.5 h-1.5 rounded bg-amber-500 animate-pulse"></span>
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wider whitespace-nowrap">
                Founding Members · Offer closes 30 September 2026
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-5">
              Join the Inner Circle
            </h2>
            <p className="text-base text-[#5a5a6a] max-w-2xl mx-auto leading-relaxed">
              We're inviting a small group of UK planners to access Envizage.AI
              at{" "}
              <strong className="text-[#1a1a2e]">
                £3.25/month per end user through 2026
              </strong>{" "}
              — 75% off the standard rate. In return, we need your honest
              feedback to build the right product. When the founding spots are
              gone, they're gone. The next person pays full price.
            </p>
          </div>
        </AnimateOnScroll>

        {/* Main two-column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
          {/* Left — The Offer */}
          <AnimateOnScroll variant="fade-left" delay={80}>
            <div className="bg-[#222222] rounded-2xl p-8 h-full flex flex-col">
              {/* Price hero */}
              <div className="mb-7">
                <div className="inline-flex items-center gap-2 bg-amber-400/15 border border-amber-400/25 rounded-full px-3 py-1 mb-5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-vip-crown-line text-xs text-amber-400"></i>
                  </div>
                  <span className="text-[11px] font-bold text-amber-400 tracking-wide uppercase whitespace-nowrap">
                    Co-Creator Founding Rate
                  </span>
                </div>

                <div className="flex items-end gap-2 mb-1">
                  <span className="text-6xl text-white leading-none">
                    £3.25
                  </span>
                  <div className="mb-2">
                    <span className="text-md text-white/50 block leading-tight">
                      /month
                    </span>
                    <span className="text-md text-white/50 block leading-tight">
                      per end user · through 2026
                    </span>
                  </div>
                </div>
                <p className="text-md text-white/40 line-through">
                  £12.99/month per end user standard rate
                </p>

                <button
                  onClick={openCtaUrl}
                  className="justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#1a1a2e] text-md font-bold p-3 rounded-full transition-colors cursor-pointer w-full mt-6"
                >
                  {/* <i className="ri-vip-crown-line flex-shrink-0"></i> */}
                  <span>Buy now</span>
                </button>

                {/* Billing scale reference */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-[10px] text-white/35 uppercase tracking-widest font-bold mb-2.5">
                    Billing scale
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { users: 1, total: "£3.25" },
                      { users: 5, total: "£16.25" },
                      { users: 10, total: "£32.50" },
                      { users: 20, total: "£65.00" },
                    ].map(({ users, total }) => (
                      <div
                        key={users}
                        className="flex items-center gap-1.5 rounded px-2.5 py-1.5 bg-white/8 border border-white/10"
                      >
                        <span className="text-[11px] text-white/45 whitespace-nowrap">
                          {users} user{users > 1 ? "s" : ""}
                        </span>
                        <span className="text-white/20 text-[10px]">→</span>
                        <span className="text-[11px] font-semibold text-amber-400/80 whitespace-nowrap">
                          {total}/mo
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <ul className="flex flex-col gap-3 flex-1">
                {perks.map((p) => (
                  <li key={p.text} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-white/10 flex-shrink-0 mt-0.5">
                      <i className={`${p.icon} text-xs text-amber-400`}></i>
                    </div>
                    <span className="text-md text-white/80 leading-relaxed">
                      {p.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Rate lock note */}
              <div className="mt-7 pt-5 border-t border-white/10">
                <p className="text-xs text-white/40 leading-relaxed">
                  After 2026, your subscription continues at the then-current
                  standard rate. No hidden fees. Cancel anytime after the
                  initial 3-month term.
                </p>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Right — What we ask */}
          <AnimateOnScroll variant="fade-right" delay={100}>
            <div className="flex flex-col h-full">
              <div className="mb-5">
                <h3 className="text-lg font-bold text-[#1a1a2e] mb-2">
                  What we ask in return
                </h3>
                <p className="text-md text-[#6a6a7a] leading-relaxed">
                  The 75% discount is reserved for active contributors. It's a
                  light commitment — most Co-Creators spend under 20 minutes a
                  month on it.
                </p>
              </div>

              <div className="flex flex-col gap-3 flex-1">
                {responsibilities.map((r, i) => (
                  <AnimateOnScroll
                    key={r.title}
                    variant="fade-up"
                    delay={i * 70}
                  >
                    <div className="flex items-start gap-4 bg-[#EFEBE5] rounded-2xl p-5 border border-[#EDE8DF] group hover:border-[#1a1a2e] transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-[#EDE8DF] flex-shrink-0 group-hover:border-[#1a1a2e] transition-colors">
                        <i className={`${r.icon} text-lg text-[#1a1a2e]`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                          <h4 className="text-md font-bold text-[#1a1a2e]">
                            {r.title}
                          </h4>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[10px] font-semibold text-[#9a9aaa] bg-white border border-[#EDE8DF] rounded px-2 py-0.5 whitespace-nowrap">
                              {r.freq}
                            </span>
                            <span className="text-[10px] font-semibold text-[#c4875a] bg-[#FAF0E4] border border-[#EDD5B0] rounded px-2 py-0.5 whitespace-nowrap">
                              {r.effort}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-[#6a6a7a] leading-relaxed">
                          {r.desc}
                        </p>
                      </div>
                    </div>
                  </AnimateOnScroll>
                ))}
              </div>

              {/* Fine print */}
              <div className="mt-4 bg-[#FDFAF6] border border-[#EDE8DF] rounded-2xl px-5 py-4 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-sm text-[#9a9aaa]"></i>
                </div>
                <p className="text-xs text-[#7a7a8a] leading-relaxed">
                  We require participation in our{" "}
                  <strong className="text-[#1a1a2e]">
                    fortnightly feedback sessions
                  </strong>{" "}
                  — they only last 5–10 minutes each. Miss three and your
                  subscription automatically reverts to the standard monthly
                  rate at the start of the following billing cycle. You'll
                  always be notified before any pricing change takes effect.
                </p>
              </div>
            </div>
          </AnimateOnScroll>
        </div>

        {/* Coming Soon */}
        <AnimateOnScroll variant="fade-up" delay={110}>
          <div className="rounded-2xl border border-dashed border-[#C8BFB0] bg-[#FDFAF6] overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-7 py-4 border-b border-dashed border-[#C8BFB0]">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded bg-[#F0EBE0]">
                  <i className="ri-rocket-2-line text-sm text-[#1a1a2e]"></i>
                </div>
                <h4 className="text-md font-bold text-[#1a1a2e]">
                  What Co-Creators Get First
                </h4>
              </div>
              <span className="inline-flex items-center gap-1.5 bg-[#222] text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap tracking-wide uppercase">
                <span className="w-1.5 h-1.5 rounded bg-amber-400 animate-pulse"></span>
                In Development
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-dashed divide-[#C8BFB0]">
              {[
                {
                  icon: "ri-file-search-line",
                  title: "Read / Understand / Populate",
                  desc: "Auto-extract client info from uploaded documents — PDFs, photos, statements. Hours of data entry, gone.",
                },
                {
                  icon: "ri-trophy-line",
                  title: "Top 3 Ways to Achieve What Matters",
                  desc: "AI-ranked action plans showing the most impactful steps toward each client's specific goals.",
                },
                {
                  icon: "ri-notification-3-line",
                  title: "Goal Tracking & Smart Alerts",
                  desc: "Ongoing goal monitoring with notifications when a client needs to act — or when they've made meaningful progress.",
                },
              ].map((f, i) => (
                <div key={i} className="flex flex-col gap-3 p-5 sm:p-6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#EDE8DF]">
                    <i className={`${f.icon} text-base text-[#1a1a2e]`}></i>
                  </div>
                  <h5 className="text-md font-bold text-[#1a1a2e] leading-snug">
                    {f.title}
                  </h5>
                  <p className="text-xs text-[#6a6a7a] leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
            <div className="px-5 sm:px-7 py-3.5 border-t border-dashed border-[#C8BFB0] bg-[#EFEBE5]/60">
              <p className="text-xs text-[#9a9aaa] text-center">
                Co-Creators vote on the order, the design, and what gets built
                next.{" "}
                <strong className="text-[#1a1a2e]">
                  Your voice shapes the roadmap.
                </strong>
              </p>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Urgency CTA */}
        {/* <AnimateOnScroll variant="fade-up" delay={130}>
          <div className="mt-10 shadow-lg bg-gradient-to-br from-[#222] to-[#000] rounded-2xl px-6 sm:px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded bg-amber-400/5 pointer-events-none"></div>
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded bg-amber-400/5 pointer-events-none"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-amber-400/15 border border-amber-400/20 rounded-full px-4 py-1.5 mb-5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-xs text-amber-400"></i>
                </div>
                <span className="text-xs font-bold text-amber-400 uppercase tracking-wider whitespace-nowrap">Founding spots are limited</span>
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 leading-snug">
                Lock in £3.25/month per end user before the spots are gone.
              </h3>
              <p className="text-md text-white/60 max-w-lg mx-auto mb-7 leading-relaxed">
                This offer closes on <strong className="text-white/80">30 September 2026</strong> — or sooner if the founding cohort fills up first. Every planner who joins after pays £12.99. Register your interest now — we'll confirm your spot within 24 hours.
              </p>
              <button
                onClick={openCtaUrl}
                className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#1a1a2e] text-md font-bold px-7 py-4 rounded-full transition-colors cursor-pointer w-full sm:w-auto"
              >
                <i className="ri-vip-crown-line flex-shrink-0"></i>
                <span>Become a Co-Creator — £3.25/month per end user</span>
              </button>
              <p className="text-xs text-white/30 mt-4">No payment required to register. We'll be in touch within 24 hours.</p>
            </div>
          </div>
        </AnimateOnScroll> */}
      </div>
    </section>
  );
}
